// audienceCategorization.js - Helper functions for categorizing audiences (Clean Version)

/**
 * Organization criteria - accountName values that indicate OA organization
 */
const OA_ORGANIZATION_NAMES = [
	"OpenAudience",
	"OpenAudience Demo",
	"openx demo",
	"openx",
	"OpenAudience Australia",
	"OpenM Org.",
];

/**
 * Provider criteria - directAudienceProvider values that indicate OA provider
 */
const OA_PROVIDER_NAMES = ["openaudience", "openaudiencecustom", "openm"];

/**
 * Check if an audience belongs to an OA organization
 * @param {Object} audience - The audience object
 * @returns {boolean} - True if organization is OA
 */
export const isOAOrganization = (audience) => {
	if (!audience || !audience.accountName) {
		return false;
	}

	const accountName = audience.accountName.trim();
	return OA_ORGANIZATION_NAMES.some(
		(name) => accountName.toLowerCase() === name.toLowerCase(),
	);
};

/**
 * Check if an audience has an OA provider
 * @param {Object} audience - The audience object
 * @returns {boolean} - True if provider is OA
 */
export const isOAProvider = (audience) => {
	if (
		!audience ||
		!audience.audienceVector ||
		!audience.audienceVector.directAudienceProvider
	) {
		return false;
	}

	const provider = audience.audienceVector.directAudienceProvider.trim();
	return OA_PROVIDER_NAMES.some(
		(name) => provider.toLowerCase() === name.toLowerCase(),
	);
};

/**
 * Categorize a single audience into the appropriate tile
 * @param {Object} audience - The audience object
 * @returns {string} - The category key
 */
export const categorizeAudience = (audience) => {
	const isOrgOA = isOAOrganization(audience);
	const isProvOA = isOAProvider(audience);

	if (!isProvOA && !isOrgOA) {
		return "non_oa_non_oa"; // Provider - OAA, Organization - OAA
	} else if (isProvOA && !isOrgOA) {
		return "oa_provider_non_oa_org"; // Provider - OA, Organization - OAA
	} else if (isProvOA && isOrgOA) {
		return "oa_provider_oa_org"; // Provider - OA, Organization - OA
	} else {
		// Edge case: non-OA provider but OA organization
		return "non_oa_provider_oa_org"; // Provider - OAA, Organization - OA
	}
};

/**
 * Get category display information
 * @param {string} categoryKey - The category key
 * @returns {Object} - Category display info
 */
export const getCategoryInfo = (categoryKey) => {
	const categories = {
		non_oa_non_oa: {
			title: "Standard Audiences",
			subtitle: "Provider: OAA • Organization: OAA",
			color: "#6c757d", // Gray
			bgColor: "#f8f9fa",
			borderColor: "#dee2e6",
		},
		oa_provider_non_oa_org: {
			title: "OpenAudience Provider",
			subtitle: "Provider: OA • Organization: OAA",
			color: "#0076BD", // Blue
			bgColor: "#e3f2fd",
			borderColor: "#0076BD",
		},
		oa_provider_oa_org: {
			title: "Full OpenAudience",
			subtitle: "Provider: OA • Organization: OA",
			color: "#28a745", // Green
			bgColor: "#d4edda",
			borderColor: "#28a745",
		},
		non_oa_provider_oa_org: {
			title: "OpenAudience Organization",
			subtitle: "Provider: OAA • Organization: OA",
			color: "#fd7e14", // Orange
			bgColor: "#fff3cd",
			borderColor: "#fd7e14",
		},
	};

	return categories[categoryKey] || categories["non_oa_non_oa"];
};

/**
 * Group audiences by category
 * @param {Array} audiences - Array of audience objects
 * @returns {Object} - Audiences grouped by category
 */
export const groupAudiencesByCategory = (audiences) => {
	if (!Array.isArray(audiences)) {
		return {};
	}

	const grouped = {
		non_oa_non_oa: [],
		oa_provider_non_oa_org: [],
		oa_provider_oa_org: [],
		non_oa_provider_oa_org: [],
	};

	audiences.forEach((audience) => {
		const category = categorizeAudience(audience);
		if (grouped[category]) {
			grouped[category].push(audience);
		} else {
			// Fallback to standard category
			grouped["non_oa_non_oa"].push(audience);
		}
	});

	return grouped;
};

/**
 * Get category statistics
 * @param {Object} groupedAudiences - Audiences grouped by category
 * @returns {Object} - Statistics for each category
 */
export const getCategoryStats = (groupedAudiences) => {
	// Handle null/undefined input
	if (!groupedAudiences || typeof groupedAudiences !== "object") {
		return {};
	}

	const stats = {};

	Object.keys(groupedAudiences).forEach((categoryKey) => {
		const audiences = groupedAudiences[categoryKey];
		const categoryInfo = getCategoryInfo(categoryKey);

		stats[categoryKey] = {
			...categoryInfo,
			count: audiences.length,
			totalUniqueUsers: audiences.reduce(
				(sum, aud) => sum + (aud.metrics?.uniqueUsers || 0),
				0,
			),
			totalRequests: audiences.reduce(
				(sum, aud) => sum + (aud.metrics?.requests || 0),
				0,
			),
			avgCompositeScore:
				audiences.length > 0
					? audiences.reduce((sum, aud) => sum + (aud.compositeScore || 0), 0) /
						audiences.length
					: 0,
			topPerformer:
				audiences.length > 0
					? audiences.reduce((best, current) =>
							(current.compositeScore || 0) > (best.compositeScore || 0)
								? current
								: best,
						)
					: null,
		};
	});

	return stats;
};

/**
 * Filter audiences within a category based on search term
 * @param {Array} audiences - Array of audiences in a category
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} - Filtered audiences
 */
export const filterAudiencesInCategory = (audiences, searchTerm) => {
	// Handle null/undefined audiences
	if (!Array.isArray(audiences)) {
		return [];
	}

	// Handle empty/null/undefined search term
	if (
		!searchTerm ||
		typeof searchTerm !== "string" ||
		searchTerm.trim() === ""
	) {
		return audiences;
	}

	const searchLower = searchTerm.trim().toLowerCase();
	return audiences.filter((audience) => {
		// Safely check each field
		const audienceName = audience.audienceName?.toLowerCase() || "";
		const accountName = audience.accountName?.toLowerCase() || "";
		const audienceStrategy = audience.audienceStrategy?.toLowerCase() || "";
		const region = audience.region?.toLowerCase() || "";

		return (
			audienceName.includes(searchLower) ||
			accountName.includes(searchLower) ||
			audienceStrategy.includes(searchLower) ||
			region.includes(searchLower)
		);
	});
};
